import { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  makeStyles,
  tokens,
  Text,
  Input,
  Button,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Skeleton,
  SkeletonItem,
  Spinner,
} from '@fluentui/react-components';
import { SearchRegular, ArrowClockwiseRegular, ArrowSortDownRegular, ArrowSortUpRegular, DesignIdeas20Regular, History20Regular, Delete20Regular, Flow16Regular, AddRegular, GridDotsRegular, Play20Regular, Pause20Regular } from '@fluentui/react-icons';
import { useQueryClient } from '@tanstack/react-query';
import { useCurrentProject } from '@/hooks/useCurrentProject';
import { useCurrentApp } from '@/hooks/useCurrentApp';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useAppWorkflows } from '@/hooks/useAppResource';
import { useDeleteWorkflows } from '@/hooks/useDeleteWorkflows';
import { useNotificationStore } from '@/stores/notificationStore';
import { syncOnboardingFromData } from '@/hooks/useOnboardingProgress';
import { ResourceErrorPage } from '@/components/shared/ResourceErrorPage';
import { EmptyState } from '@/components/shared/EmptyState';
import { AppOverviewHeader } from '@/components/shared/AppOverviewHeader';
import { PageSection } from '@/components/shared/PageSection';
import { OverflowMenu, type OverflowMenuItem } from '@/components/shared/OverflowMenu';
import {
  EntityNameCell,
  DotStatusBadge,
  TablePaginationFooter,
  DataTablePanel,
  usePagination,
  type DotStatusKind,
} from '@/components/shared/DataTable';
import { WorkflowBuilderHero } from '@/components/WorkflowBuilderHero';
import { TemplateGalleryDialog } from '@/components/WorkflowDesigner/panels';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { openCreateWorkflowDialog } from '@/stores/createWorkflowDialogStore';
import { useCreateWorkflow, generateUniqueName } from '@/hooks/useCreateWorkflow';
import { CREATE_FLAG_PARAM } from '@/routes/queryParams';
import { DeleteConfirmDialog } from '@/components/common/DeleteConfirmDialog';
import { useTelemetry } from '@/hooks/useTelemetry';
import { paths } from '@/routes';
import { extractWorkflowName } from '@/types/logicApps';
import { useAppPermission } from '@/hooks/useAppPermission';
import { formatDateTime } from '@/utils/dateTime';
import { visuallyHiddenStyle } from '@/styles/a11y';
import { useEnvironmentSettings, useUpdateEnvironmentSettings } from '@/hooks/useEnvironmentSettings';

type TemplateSource = 'workflow-list-hero' | 'workflow-list-toolbar' | 'workflow-list-empty-state';

const mapWorkflowState = (state: string | undefined): DotStatusKind => {
  switch (state) {
    case 'Enabled':
      return 'Active';
    case 'Disabled':
      return 'Inactive';
    default:
      return 'Inactive';
  }
};

const useStyles = makeStyles({
  page: {
    // AppLayout's `content` wrapper already pads with XXXL — don't double up here.
    // Vertical rhythm is owned by each section (PageSection.marginTop = L),
    // not by a flex `gap` here — adding one stacks with PageSection's margin
    // and inflates the space below the AppOverviewHeader vs. other pages.
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    // Constrain to the viewport so the table area below can own its own
    // scrollbar instead of the whole page scrolling under the sticky header.
    minHeight: 0,
    overflow: 'hidden',
  },
  // PageSection is content-sized by default. Inside `.page` (which is
  // `overflow: hidden`), we need to give the section a bounded flex slot
  // so the inner `DataTablePanel` can claim the remaining height and
  // pin its footer to the viewport bottom.
  tableSection: {
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  emptyActionRow: {
    display: 'flex',
    justifyContent: 'center',
    gap: tokens.spacingHorizontalS,
    paddingBlock: tokens.spacingVerticalL,
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
  },
  toolbarLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
  },
  toolbarRight: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  search: {
    minWidth: '320px',
  },
  metadata: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground2,
  },
  colName: { width: '65%' },
  colDateModified: { width: '15%' },
  colStatus: { width: '13%' },
  actionsCell: {
    width: '7%',
    whiteSpace: 'nowrap',
    textAlign: 'right',
    paddingRight: tokens.spacingHorizontalM,
  },
  srOnly: visuallyHiddenStyle,
  skeletonHero: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalM,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  skeletonTextarea: {
    height: '56px',
    borderRadius: tokens.borderRadiusMedium,
  },
  skeletonSuggestionRow: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
  },
  skeletonSuggestion: {
    width: '120px',
    height: '28px',
    borderRadius: tokens.borderRadiusMedium,
  },
  skeletonFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  skeletonHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  skeletonTableRow: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    alignItems: 'center',
    padding: `${tokens.spacingVerticalS} 0`,
  },
  sortableHeader: {
    cursor: 'pointer',
    userSelect: 'none' as const,
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
    background: 'none',
    border: 'none',
    padding: 0,
    font: 'inherit',
    color: 'inherit',
    '&:hover': {
      color: tokens.colorBrandForeground1,
    },
  },
});

/**
 * Content shimmer shown *inside* the page while workflows are loading.
 *
 * The page-level `AppOverviewHeader` stays mounted above this during load so
 * the page chrome doesn't disappear and reappear — matching the loading
 * behavior of `AppListPage`, `Analytics`, Connections, and Parameters.
 */
const WorkflowListSkeleton = ({ isAssistantEnabled }: { isAssistantEnabled: boolean }) => {
  const styles = useStyles();
  return (
    <div role="status" aria-label="Loading workflows">
      <Skeleton>
        {isAssistantEnabled && (
          <div className={styles.skeletonHero}>
            <SkeletonItem size={20} style={{ width: '260px' }} />
            <SkeletonItem size={12} style={{ width: '380px' }} />
            <SkeletonItem className={styles.skeletonTextarea} />
            <div className={styles.skeletonSuggestionRow}>
              <SkeletonItem size={12} style={{ width: '60px' }} />
              <SkeletonItem className={styles.skeletonSuggestion} />
              <SkeletonItem className={styles.skeletonSuggestion} />
              <SkeletonItem className={styles.skeletonSuggestion} />
            </div>
            <div className={styles.skeletonFooter}>
              <SkeletonItem size={16} style={{ width: '140px' }} />
              <SkeletonItem size={32} style={{ width: '200px', borderRadius: tokens.borderRadiusMedium }} />
            </div>
          </div>
        )}

        {/* Table header shimmer */}
        <div className={styles.skeletonHeader}>
          <SkeletonItem size={20} style={{ width: '100px' }} />
          <SkeletonItem size={28} style={{ width: '200px', borderRadius: tokens.borderRadiusMedium }} />
        </div>

        {/* Table rows shimmer */}
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className={styles.skeletonTableRow}>
            <SkeletonItem size={16} style={{ flex: 1 }} />
            <SkeletonItem size={16} style={{ width: '80px' }} />
            <SkeletonItem size={16} style={{ width: '48px' }} />
          </div>
        ))}
      </Skeleton>
    </div>
  );
};

export const WorkflowListPage = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { trackEvent } = useTelemetry();
  const { projectName, locationName } = useCurrentProject();
  const { appName, app } = useCurrentApp();
  useDocumentTitle(['Workflows', appName, projectName]);
  const { tenantId } = useParams<{ tenantId: string }>();
  const { data: workflows, isLoading, error, isFetching } = useAppWorkflows(app ?? null);
  const [search, setSearch] = useState('');
  // `?create=1` deep-links to the global CreateWorkflowDialogHost so shared
  // URLs that pre-date the global dialog still pop the create flow.
  const [searchParams, setSearchParams] = useSearchParams();
  const [workflowToDelete, setWorkflowToDelete] = useState<string | null>(null);
  // Track which workflow is currently having its state updated (enable/disable)
  const [updatingWorkflowName, setUpdatingWorkflowName] = useState<string | null>(null);
  // Local-only favorites (not persisted) — mirrors the Apps + Projects tables.
  const [favorites, setFavorites] = useState<Set<string>>(() => new Set());
  const toggleFavorite = useCallback((name: string, next: boolean) => {
    setFavorites((prev) => {
      const updated = new Set(prev);
      if (next) updated.add(name);
      else updated.delete(name);
      return updated;
    });
  }, []);
  const addNotification = useNotificationStore((s) => s.addNotification);
  const hasWorkflows = useMemo(() => !!(workflows && workflows.length > 0), [workflows]);
  const { data: settings, isLoading: isSettingsLoading, isFetching: isSettingsFetching, isError: settingsInError, refetch: refetchSettings } = useEnvironmentSettings(projectName!, appName!);
  const { canEdit, isLoading: isPermissionsLoading } = useAppPermission();
  const updateSettingsMutation = useUpdateEnvironmentSettings(projectName!, appName!);

  useEffect(() => {
    if (searchParams.get(CREATE_FLAG_PARAM) !== '1') return;
    // Wait until permissions resolve before deciding. Otherwise the effect
    // fires once with canEdit=false (loading) and strips the flag, then
    // fires again with canEdit=true but the flag is already gone — leaving
    // the user staring at a list page with no dialog despite their click.
    if (isPermissionsLoading) return;
    if (canEdit) {
      openCreateWorkflowDialog({ source: 'deep-link' });
    }
    const next = new URLSearchParams(searchParams);
    next.delete(CREATE_FLAG_PARAM);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, canEdit, isPermissionsLoading]);

  const deleteWorkflowMutation = useDeleteWorkflows(
    { appName: appName ?? '', projectName: projectName ?? '' },
    {
      onSuccess: async (workflowName) => {
        // Invalidate the exact key shape used by useAppResource / useAppWorkflows
        // so the list refreshes immediately after the delete.
        if (projectName && appName) {
          await queryClient.invalidateQueries({
            queryKey: ['workflows', projectName.toLowerCase(), appName.toLowerCase()],
          });
        }
        setWorkflowToDelete(null);
        addNotification({
          type: 'success',
          title: 'Workflow deleted',
          message: `Successfully deleted "${workflowName}".`,
        });
        // Note: useDeleteWorkflows already emits 'workflow.deleted' internally;
        // no duplicate trackEvent call here.
      },
      onError: (error) => {
        setWorkflowToDelete(null);
        addNotification({
          type: 'error',
          title: 'Failed to delete workflow',
          message: error.message ?? 'Unknown error',
        });
      },
    },
  );

  // Auto-sync onboarding: we're in an app so steps 1 & 2 are done
  useEffect(() => {
    if (!isLoading) {
      syncOnboardingFromData({ hasProjects: true, hasApps: true, hasWorkflows });
    }
  }, [hasWorkflows, isLoading]);

  const canBuildDesignerPath = useMemo(() => !!(locationName && projectName && appName), [locationName, projectName, appName]);

  const getDesignerPath = useCallback(
    (workflowName: string) => {
      if (canBuildDesignerPath) {
        return paths.appWorkflowDesigner(locationName!, projectName!, appName!, workflowName, tenantId);
      }
      return '#';
    },
    [locationName, projectName, appName, canBuildDesignerPath, tenantId],
  );

  // Hero handlers delegate to the global CreateWorkflowDialogHost (mounted
  // by AppLayout), which owns the mutation, error state, and navigation.
  const handleBuild = useCallback(
    (prompt: string) => {
      openCreateWorkflowDialog({ prompt, source: 'workflow-list-hero' });
    },
    [],
  );

  const isAssistantEnabled = useFeatureFlag('workflowAssistant');

  // Template gallery: open the gallery directly, let the user pick a template AND
  // enter a workflow name in the preview, then create+apply in one shot.
  const isTemplatesEnabled = useFeatureFlag('workflowTemplates');
  const [isTemplateGalleryOpen, setIsTemplateGalleryOpen] = useState(false);
  const [templateSource, setTemplateSource] = useState<TemplateSource>('workflow-list-hero');

  const handleBuildFromTemplate = useCallback((source: TemplateSource = 'workflow-list-hero') => {
    setTemplateSource(source);
    setIsTemplateGalleryOpen(true);
  }, []);

  const handleTemplateGalleryDismiss = useCallback(() => {
    setIsTemplateGalleryOpen(false);
  }, []);

  const defaultTemplateWorkflowName = useMemo(
    () => generateUniqueName(workflows ?? [], 'myWorkflow'),
    [workflows]
  );

  // The create call is an upsert (deployWorkflowArtifacts), so a duplicate name
  // would silently overwrite an existing workflow. Feed the current names into
  // the gallery's name field so duplicates are blocked client-side, matching
  // CreateWorkflowDialog's validation.
  const existingWorkflowNames = useMemo(
    () => (workflows ?? []).map((w) => extractWorkflowName(w.name)),
    [workflows]
  );

  // The template ID and source are captured at submit time and consumed by
  // onSuccess of the create-workflow mutation, so they must outlive the
  // async mutation (state may change before it resolves if the user opens
  // the gallery again from a different entry point).
  const pendingTemplateId = useRef<string | null>(null);
  const pendingTemplateSource = useRef<TemplateSource>('workflow-list-hero');

  const createFromTemplateMutation = useCreateWorkflow({
    appInfo: { projectName: projectName ?? '', appName: appName ?? '' },
    onSuccess: (workflowName) => {
      trackEvent('workflow.create.succeeded', {
        workflowName,
        source: pendingTemplateSource.current,
        flow: 'template-gallery',
      });
      if (projectName && appName) {
        queryClient.invalidateQueries({
          queryKey: ['workflows', projectName.toLowerCase(), appName.toLowerCase()],
        });
      }
      setIsTemplateGalleryOpen(false);
      if (canBuildDesignerPath && pendingTemplateId.current) {
        const designerPath = getDesignerPath(workflowName);
        navigate(`${designerPath}?applyTemplate=${encodeURIComponent(pendingTemplateId.current)}`);
        pendingTemplateId.current = null;
      }
    },
    onError: (err) => {
      addNotification({
        type: 'error',
        title: 'Failed to create workflow',
        message: err.message ?? 'Unknown error',
      });
    },
  });

  const handleCreateFromTemplate = useCallback(
    (templateId: string, workflowName: string) => {
      pendingTemplateId.current = templateId;
      pendingTemplateSource.current = templateSource;
      trackEvent('workflow.create.started', {
        source: pendingTemplateSource.current,
        flow: 'template-gallery',
        templateId,
      });
      createFromTemplateMutation.mutate(workflowName);
    },
    [createFromTemplateMutation, templateSource, trackEvent]
  );

  type WorkflowSortColumn = 'name' | 'state';
  const [sortColumn, setSortColumn] = useState<WorkflowSortColumn>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const handleSort = useCallback((column: WorkflowSortColumn) => {
    if (sortColumn === column) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  }, [sortColumn]);

  const filteredWorkflows = useMemo(() => {
    let result = workflows ?? [];
    if (search.trim()) {
      const query = search.toLowerCase();
      result = result.filter((w) => extractWorkflowName(w.name).toLowerCase().includes(query));
    }
    const dir = sortDirection === 'asc' ? 1 : -1;
    return [...result].sort((a, b) => {
      switch (sortColumn) {
        case 'name':
          return dir * extractWorkflowName(a.name).localeCompare(extractWorkflowName(b.name));
        case 'state':
          return dir * (a.properties?.state ?? '').localeCompare(b.properties?.state ?? '');
        default:
          return 0;
      }
    });
  }, [workflows, search, sortColumn, sortDirection]);

  const pagination = usePagination<typeof filteredWorkflows[number]>({
    totalItems: filteredWorkflows.length,
    storageKey: 'otto.workflowList.pageSize',
    resetDeps: [search, sortColumn, sortDirection],
  });
  const pagedWorkflows = useMemo(
    () => pagination.paginate(filteredWorkflows),
    [pagination, filteredWorkflows],
  );

  const handleRefresh = useCallback((fetchSettings = true) => {
    // invalidateQueries (not resetQueries) keeps the previously-fetched data
    // visible while a background refetch runs. The Refresh button already
    // swaps to a spinner via `isFetching`, so the loading affordance is
    // preserved without unmounting the entire page back to the full skeleton.
    queryClient.invalidateQueries({ queryKey: ['workflows', projectName?.toLowerCase(), appName?.toLowerCase()] });
    if (fetchSettings) {
      refetchSettings();
    }
  }, [queryClient, projectName, appName, refetchSettings]);

  const handleUpdateWorkflowState = useCallback(
    async (workflowName: string, disable: boolean) => {
      setUpdatingWorkflowName(extractWorkflowName(workflowName));
      try {
        const settingName = `Workflows.${extractWorkflowName(workflowName)}.FlowState`;
        const newValue = disable ? 'Disabled' : 'Enabled';
        const existingSettings = (settings ?? []).map((setting) => ({
          name: setting.name,
          value: setting.value ?? '',
        }));
        const existingIndex = existingSettings.findIndex(
          setting => setting.name.toLowerCase() === settingName.toLowerCase()
        );

        let patchedSettings: { name: string; value: string }[];
        if (existingIndex !== -1) {
          // Replace existing setting
          patchedSettings = existingSettings.map((setting, index) =>
            index === existingIndex ? { ...setting, value: newValue } : setting
          );
        } else {
          // Add new setting
          patchedSettings = [...existingSettings, { name: settingName, value: newValue }];
        }

        await updateSettingsMutation.mutateAsync({ properties: { settings: patchedSettings } });

        handleRefresh(/* fetchSettings */ false);
        addNotification({
          type: 'success',
          title: `Workflow ${disable ? 'disabled' : 'enabled'}`,
          message: `Successfully ${disable ? 'disabled' : 'enabled'} "${extractWorkflowName(workflowName)}".`,
        });
        trackEvent('workflow.updateState.succeeded', {
          workflowName,
          newState: disable ? 'Disabled' : 'Enabled',
        });
      } catch (error: unknown) {
        setUpdatingWorkflowName(null);
        addNotification({
          type: 'error',
          title: `Failed to ${disable ? 'disable' : 'enable'} workflow`,
          message: error instanceof Error ? error.message : 'Unknown error',
        });
        trackEvent('workflow.updateState.failed', {
          workflowName,
          newState: disable ? 'Disabled' : 'Enabled',
          error: error instanceof Error ? error.message : 'Unknown error',
        });
      } finally {
        setUpdatingWorkflowName(null);
      }
    },
    [settings, updateSettingsMutation, handleRefresh, addNotification, trackEvent],
  );

  if (error) {
    return <ResourceErrorPage resourceType="workflow" error={error} onRetry={handleRefresh} />;
  }

  if (isLoading) {
    return (
      <div className={styles.page}>
        {appName && (
          <AppOverviewHeader
            name={appName}
            app={app}
            region={locationName}
            workflowCount={workflows?.length ?? 0}
          />
        )}
        <WorkflowListSkeleton isAssistantEnabled={isAssistantEnabled} />
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {appName && hasWorkflows && (
        <AppOverviewHeader
          name={appName}
          app={app}
          region={locationName}
          workflowCount={workflows?.length ?? 0}
        />
      )}
      {isAssistantEnabled && (
        <WorkflowBuilderHero
          hasWorkflows={hasWorkflows}
          isCreating={false}
          error={null}
          disabled={!canEdit}
          onBuild={handleBuild}
          {...(isTemplatesEnabled ? { onBuildFromTemplate: () => handleBuildFromTemplate('workflow-list-hero') } : {})}
        />
      )}

      {!hasWorkflows && canEdit && !isPermissionsLoading && (
        <div className={styles.emptyActionRow}>
          {isTemplatesEnabled && (
            <Button
              appearance="outline"
              icon={<GridDotsRegular />}
              onClick={() => handleBuildFromTemplate('workflow-list-empty-state')}
            >
              Build from templates
            </Button>
          )}
          <Button
            appearance="primary"
            icon={<AddRegular />}
            onClick={() => openCreateWorkflowDialog({ source: 'workflow-list-empty-state' })}
          >
            Create workflow
          </Button>
        </div>
      )}

      {hasWorkflows && (
        <PageSection title="Workflows" className={styles.tableSection}>
          <div className={styles.toolbar}>
            <div className={styles.toolbarLeft}>
              <Input
                className={styles.search}
                size="small"
                contentBefore={<SearchRegular />}
                placeholder="Search workflows..."
                value={search}
                onChange={(_e, data) => setSearch(data.value)}
              />
            </div>
            <div className={styles.toolbarRight}>
              <Button
                appearance="subtle"
                size="small"
                icon={isFetching ? <Spinner size="tiny" /> : <ArrowClockwiseRegular />}
                onClick={() => handleRefresh()}
                disabled={isFetching}
              >
                Refresh
              </Button>
              {canEdit && !isPermissionsLoading ? (
                <>
                  {isTemplatesEnabled && (
                    <Button
                      appearance="outline"
                      size="small"
                      icon={<GridDotsRegular />}
                      onClick={() => handleBuildFromTemplate('workflow-list-toolbar')}
                    >
                      Build from templates
                    </Button>
                  )}
                  <Button
                    appearance="primary"
                    size="small"
                    icon={<AddRegular />}
                    onClick={() => openCreateWorkflowDialog({ source: 'workflow-list-toolbar' })}
                  >
                    Create workflow
                  </Button>
                </>
              ) : null}
            </div>
          </div>

          {filteredWorkflows.length === 0 ? (
            <EmptyState
              title="No workflows found"
              description="Try a different search term."
            />
          ) : (
            <DataTablePanel
              bodyTestId="workflow-table-scroll-area"
              flushFooter
              footer={
                <TablePaginationFooter
                  totalItems={filteredWorkflows.length}
                  currentPage={pagination.page}
                  pageSize={pagination.pageSize}
                  onPageChange={pagination.setPage}
                  onPageSizeChange={pagination.setPageSize}
                />
              }
            >
              <Table aria-label="Workflows table">
          <TableHeader>
            <TableRow>
              <TableHeaderCell className={styles.colName} aria-sort={sortColumn === 'name' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>
                <button type="button" className={styles.sortableHeader} onClick={() => handleSort('name')}>
                  Name {sortColumn === 'name' && (sortDirection === 'asc' ? <ArrowSortUpRegular fontSize={12} /> : <ArrowSortDownRegular fontSize={12} />)}
                </button>
              </TableHeaderCell>
              <TableHeaderCell className={styles.colDateModified}>Date modified</TableHeaderCell>
              <TableHeaderCell className={styles.colStatus} aria-sort={sortColumn === 'state' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}>
                <button type="button" className={styles.sortableHeader} onClick={() => handleSort('state')}>
                  Status {sortColumn === 'state' && (sortDirection === 'asc' ? <ArrowSortUpRegular fontSize={12} /> : <ArrowSortDownRegular fontSize={12} />)}
                </button>
              </TableHeaderCell>
              <TableHeaderCell className={styles.actionsCell}>
                <span className={styles.srOnly}>Actions</span>
              </TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pagedWorkflows.map((workflow) => {
              const wfName = extractWorkflowName(workflow.name);
              const isDisabled = workflow.properties?.state === 'Disabled';
              const menuItems: OverflowMenuItem[] = [{
                label: 'Open designer',
                icon: DesignIdeas20Regular,
                onClick: () => {
                  trackEvent('navigation.workflow.openDesigner', { workflowName: wfName });
                  if (canBuildDesignerPath) {
                    navigate(getDesignerPath(wfName));
                  }
                },
              },
              {
                label: 'View runs',
                icon: History20Regular,
                onClick: () => {
                  trackEvent('navigation.workflow.viewRuns', { workflowName: wfName });
                  if (canBuildDesignerPath) {
                    navigate(`${getDesignerPath(wfName)}/runs`);
                  }
                },
              }];

              if (isPermissionsLoading) {
                // While loading permissions, show only non-destructive actions to avoid potential confusion
                menuItems.push({
                  label: isDisabled ? 'Enable' : 'Disable',
                  icon: isDisabled ? Play20Regular : Pause20Regular,
                  disabled: true,
                  onClick: () => {},
                });
                menuItems.push({
                  label: 'Delete',
                  icon: Delete20Regular,
                  danger: true,
                  dividerBefore: true,
                  disabled: true,
                  onClick: () => {},
                });
              } else if (canEdit) {
                menuItems.push({
                  label: isDisabled ? 'Enable' : 'Disable',
                  icon: isDisabled ? Play20Regular : Pause20Regular,
                  disabled: isSettingsLoading || isSettingsFetching || settingsInError || updateSettingsMutation.isPending,
                  onClick: () => handleUpdateWorkflowState(workflow.name, !isDisabled),
                });
                menuItems.push({
                  label: 'Delete',
                  icon: Delete20Regular,
                  danger: true,
                  dividerBefore: true,
                  onClick: () => {
                    setWorkflowToDelete(wfName);
                  },
                });
              }

              return (
              <TableRow key={workflow.name}>
                <TableCell className={styles.colName}>
                  <EntityNameCell
                    icon={<Flow16Regular />}
                    name={wfName}
                    href={getDesignerPath(wfName)}
                    onClick={() => trackEvent('navigation.workflow.selected', { workflowName: wfName })}
                    isFavorite={favorites.has(wfName)}
                    onToggleFavorite={(next) => toggleFavorite(wfName, next)}
                  />
                </TableCell>
                <TableCell className={styles.colDateModified}>
                  <Text className={styles.metadata}>{formatDateTime(workflow.properties?.changedTime, 'full', { fallback: '—' })}</Text>
                </TableCell>
                <TableCell className={styles.colStatus}>
                  {updatingWorkflowName === wfName ? (
                    <Skeleton aria-label="Updating status">
                      <SkeletonItem size={16} style={{ width: '60px' }} />
                    </Skeleton>
                  ) : (
                    <DotStatusBadge status={mapWorkflowState(workflow.properties?.state)} />
                  )}
                </TableCell>
                <TableCell className={styles.actionsCell}>
                  <OverflowMenu items={menuItems} />
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
            </DataTablePanel>
          )}
        </PageSection>
      )}

      <DeleteConfirmDialog
        open={workflowToDelete !== null}
        onClose={() => {
          // Don't dismiss while the mutation is mid-flight.
          if (!deleteWorkflowMutation.isPending) {
            setWorkflowToDelete(null);
          }
        }}
        onConfirm={() => {
          if (workflowToDelete) {
            deleteWorkflowMutation.mutate(workflowToDelete);
          }
        }}
        itemType="workflow"
        itemNames={workflowToDelete ? [workflowToDelete] : []}
        isDeleting={deleteWorkflowMutation.isPending}
      />

      <TemplateGalleryDialog
        open={isTemplateGalleryOpen}
        onDismiss={handleTemplateGalleryDismiss}
        requireWorkflowName
        defaultWorkflowName={defaultTemplateWorkflowName}
        existingWorkflowNames={existingWorkflowNames}
        isCreating={createFromTemplateMutation.isPending}
        onCreateFromTemplate={handleCreateFromTemplate}
      />
    </div>
  );
};